import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import SectionHeader from '@/components/common/SectionHeader';
import apiService from '@/services/api';

const SectorsPage = () => {
  const [sectors, setSectors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/public/sectors');
        if (response?.data && Array.isArray(response.data)) {
          setSectors(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch sectors:', err);
        setError('Failed to load sectors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSectors();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Sectors & Industries | Virewire Property Services</title>
        <meta name="description" content="Professional B2B property services for letting agents, healthcare, education, hospitality, and more sectors." />
        <meta property="og:title" content="Sectors & Industries | Virewire Property Services" />
        <meta property="og:description" content="Professional B2B property services for letting agents, healthcare, education, hospitality, and more sectors." />
        <link rel="canonical" href="https://virewire.com/sectors" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16 lg:py-24">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Industries We Serve
          </h1>
          <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
            Specialized property maintenance and cleaning solutions for diverse commercial sectors across the UK.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-soft-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg text-center">
              {error}
            </div>
          ) : sectors.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sectors.map((sector) => (
                <div 
                  key={sector.id}
                  className="bg-white rounded-2xl p-8 shadow-card hover:shadow-card-lg transition-all duration-300 border border-border"
                >
                  <div className="w-16 h-16 mb-6 rounded-lg bg-lime-100 flex items-center justify-center text-3xl">
                    {sector.icon_key || '✨'}
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-3">
                    {sector.name}
                  </h3>
                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {sector.description || 'Professional property services tailored for your sector.'}
                  </p>
                  <Button variant="outline" asChild className="w-full">
                    <Link to={`/quote?sector=${sector.id}`}>
                      Get Quote <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-text-secondary">
              <p className="text-lg">No sectors available at this time.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            title="Why Virewire for Your Sector"
            subtitle="Proven expertise across multiple commercial industries"
            centered
          />
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-lime-500 flex items-center justify-center text-white font-bold">
                ✓
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                Industry Expertise
              </h3>
              <p className="text-text-secondary">
                Deep understanding of specific sector needs and compliance requirements
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-lime-500 flex items-center justify-center text-white font-bold">
                ✓
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                Reliable Delivery
              </h3>
              <p className="text-text-secondary">
                Consistent, professional service you can count on, every time
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-lime-500 flex items-center justify-center text-white font-bold">
                ✓
              </div>
              <h3 className="text-xl font-semibold text-primary mb-2">
                Multi-Location Support
              </h3>
              <p className="text-text-secondary">
                National coverage for portfolio properties and multi-site operations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's discuss how Virewire can support your sector's property service needs.
          </p>
          <Button variant="secondary" size="lg" asChild>
            <Link to="/service">
              Get Instant Quote <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default SectorsPage;
